import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowRight, PlayCircle, Sparkles, ChevronDown } from 'lucide-react';
import PrinterStrip from './PrinterStrip';
import RegistrationMark from './RegistrationMark';
import BrandLogo from './BrandLogo';

const HERO_STATS = [
  { value: '+400', accent: '', label: 'Customers' },
  { value: '+100', accent: 'y', label: 'Years combined' },
  { value: '#1', accent: '', label: 'US Support' },
];

const HeroBackground = () => (
  <>
    {/* Pure white canvas — matches the navbar section */}

    {/* Registration marks in corners (like real print proofs) */}
    <RegistrationMark className="absolute top-24 left-6 opacity-50" size={24} />
    <RegistrationMark className="absolute top-24 right-6 opacity-50" size={24} />
    <RegistrationMark className="absolute bottom-10 left-6 opacity-50" size={24} />
    <RegistrationMark className="absolute bottom-10 right-6 opacity-50" size={24} />
  </>
);

const HeroCopy = () => (
  <div className="md:col-span-7 animate-fade-up">
    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/70 backdrop-blur border border-[#0A2A63]/10 text-[#0A2A63] mb-7 shadow-sm">
      <Sparkles className="h-3.5 w-3.5 text-[#FF4E50]" />
      <span className="text-xs font-semibold tracking-widest uppercase">Smart Technology, Support That You Can Count On</span>
      <span className="ml-2 flex items-center gap-1">
        {['#0A2A63', '#1E4CB8', '#FF4E50', '#050F2E'].map((c) => (
          <span key={c} className="h-2 w-2 rounded-full" style={{ backgroundColor: c }} />
        ))}
      </span>
    </div>

    <h1 className="text-[34px] md:text-[54px] leading-[1.02] font-black tracking-tight text-[#050F2E]">
      We don&apos;t just sell printing technology.
      <span className="block mt-2">
        We make it{' '}
        <span className="relative inline-block">
          <span className="relative z-10 pn-gradient-text">
            work for you
          </span>
          <span className="absolute left-0 right-0 -bottom-1 h-3 bg-[#FF4E50]/25 rounded-sm -z-0" />
        </span>
        .
      </span>
    </h1>

    <p className="mt-7 text-lg md:text-xl text-slate-600 max-w-xl leading-relaxed">
      The best inkjet, spectrophotometer, and color management solutions for your business.
      Built by printers, for printers.
    </p>

    {/* Calibration strip */}
    <div className="mt-8 max-w-lg">
      <div className="flex items-center gap-3 mb-2">
        <span className="text-[10px] font-mono uppercase tracking-widest text-slate-500 font-bold">Calibration · ISO 13655</span>
        <span className="h-px flex-1 bg-slate-200" />
        <span className="text-[10px] font-mono uppercase tracking-widest text-[#FF4E50] font-bold">ΔE &lt; 1.0</span>
      </div>
      <PrinterStrip />
    </div>

    <div className="mt-9 flex flex-wrap items-center gap-4">
      <Link to="/shop">
        <Button className="bg-[#0A2A63] hover:bg-[#061a3f] text-white rounded-full h-14 px-8 text-base font-bold btn-transition shadow-lg hover:shadow-xl group">
          View Portfolio
          <ArrowRight className="h-5 w-5 ml-2 group-hover:translate-x-1 transition-transform" />
        </Button>
      </Link>
      <Link to="/contact">
        <Button variant="outline" className="rounded-full h-14 px-7 text-base font-bold border-2 border-[#0A2A63] text-[#0A2A63] hover:bg-[#0A2A63] hover:text-white btn-transition bg-white/70 backdrop-blur">
          <PlayCircle className="h-5 w-5 mr-2" />
          Specialist contacts
        </Button>
      </Link>
    </div>

    {/* Stats strip */}
    <div className="mt-14 grid grid-cols-3 max-w-md gap-4">
      {HERO_STATS.map((s, i) => (
        <div key={s.label} className={i > 0 ? 'border-l border-slate-200 pl-4' : ''}>
          <p className="text-3xl md:text-4xl font-black text-[#0A2A63]">
            {s.value}
            {s.accent && <span className="text-[#FF4E50]">{s.accent}</span>}
          </p>
          <p className="text-xs uppercase tracking-widest text-slate-500 mt-1">{s.label}</p>
        </div>
      ))}
    </div>
  </div>
);

const HeroStarShowcase = ({ mouse }) => (
  <div className="md:col-span-5 relative h-[520px] md:h-[640px]">
    {/* Background card */}
    <div
      className="absolute inset-0 rounded-[28px] overflow-hidden shadow-2xl"
      style={{
        background: 'linear-gradient(135deg, #050F2E 0%, #0A2A63 55%, #050F2E 100%)',
        transform: `perspective(1200px) rotateY(${(mouse.x - 0.5) * -6}deg) rotateX(${(mouse.y - 0.5) * 6}deg)`,
      }}
    >
      <div className="absolute inset-0 opacity-50 grain-overlay" />

      {/* Star product badge */}
      <div className="absolute top-5 left-5 z-10 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-[10px] font-black uppercase tracking-widest text-white shadow-lg bg-gradient-to-r from-[#FF4E50] to-[#E03B3D]" data-testid="hero-star-badge">
        <Sparkles className="h-3 w-3" /> Star product Nº1
      </div>
      <span className="absolute top-6 right-5 z-10 text-[9px] font-mono text-white/50 tracking-widest">PN·22</span>

      {/* PrintFactory hero visual */}
      <img
        src="https://static.prod-images.emergentagent.com/jobs/fc296bd7-6c7a-4504-92b7-950d95a6db4c/images/ce186f45c049c4db681a039495ddea4c7fad61517479580b89ffbf82a227b9aa.png"
        alt="PrintFactory cloud color workflow"
        data-testid="hero-printfactory-image"
        className="absolute top-[13%] left-0 right-0 w-full object-cover animate-float"
        style={{ maskImage: 'linear-gradient(180deg, black 78%, transparent 100%)', WebkitMaskImage: 'linear-gradient(180deg, black 78%, transparent 100%)' }}
      />

      {/* PrintFactory identity */}
      <div className="absolute bottom-[152px] left-6 right-6 flex items-end justify-between">
        <div>
          <p className="text-[10px] uppercase tracking-[0.25em] text-[#FF4E50] font-black">Cloud Color Workflow</p>
          <BrandLogo brand="printfactory" chip chipClassName="px-2.5 py-1.5" imgClassName="h-7" className="mt-1.5" testId="hero-printfactory-logo" />
          <p className="text-xs text-white/70 mt-2 max-w-[240px]">By printers, for printers — RIP, calibration & automation in one cloud.</p>
        </div>
      </div>

      {/* Bottom color strip inside card */}
      <div className="absolute bottom-[118px] left-6 right-6">
        <PrinterStrip />
      </div>

      {/* Stat + CTA row */}
      <div className="absolute bottom-6 left-6 right-6 flex items-center gap-3">
        <div className="flex-1 bg-white/10 border border-white/15 backdrop-blur-sm rounded-2xl px-4 py-3">
          <p className="text-2xl font-black text-white leading-none">150,000<span className="text-[#FF4E50]">+</span></p>
          <p className="text-[9px] uppercase tracking-widest text-white/60 font-bold mt-1">Satisfied printers</p>
        </div>
        <Link to="/printfactory" className="flex-1">
          <Button data-testid="hero-printfactory-cta" className="w-full bg-[#FF4E50] hover:bg-[#E03B3D] text-white rounded-2xl h-14 font-bold btn-transition shadow-lg shadow-[#FF4E50]/30 group/pf">
            Discover it
            <ArrowRight className="h-4 w-4 ml-2 group-hover/pf:translate-x-1 transition-transform" />
          </Button>
        </Link>
      </div>
    </div>

    {/* Decorative rings */}
    <div className="absolute -bottom-4 -left-4 h-24 w-24 rounded-full border-2 border-dashed border-[#FF4E50] animate-spin-slow opacity-70" />
    <div className="absolute -top-4 -right-4 h-16 w-16 rounded-full border-2 border-dashed border-[#0A2A63] animate-spin-slow opacity-70" style={{ animationDirection: 'reverse' }} />
  </div>
);

const Hero = () => {
  const [mouse, setMouse] = useState({ x: 0.5, y: 0.5 });

  useEffect(() => {
    const handler = (e) => {
      setMouse({
        x: e.clientX / window.innerWidth,
        y: e.clientY / window.innerHeight,
      });
    };
    window.addEventListener('mousemove', handler);
    return () => window.removeEventListener('mousemove', handler);
  }, []);

  return (
    <section className="relative min-h-[100vh] pt-32 pb-16 overflow-hidden bg-white">
      <HeroBackground />

      <div className="container-pn relative grid md:grid-cols-12 gap-10 items-center">
        <HeroCopy />
        <HeroStarShowcase mouse={mouse} />
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center text-slate-500">
        <span className="text-xs uppercase tracking-widest mb-1">Scroll</span>
        <ChevronDown className="h-5 w-5 animate-bounce" />
      </div>
    </section>
  );
};

export default Hero;
